import React, { useState } from 'react';
import styled from 'styled-components';
import { usePrivy } from '@privy-io/react-auth';
import { supabase } from '../../../utils/supabase';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 10001;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: #0c0814;
  border: 2px solid #9535fc;
  border-radius: 12px;
  padding: 24px;
  max-width: 480px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const Header = styled.h2`
  color: #21c95e;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  margin: 0 0 8px 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #a1a1aa;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  margin: 0 0 20px 0;
  text-align: center;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  color: #c9b5ff;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const RequiredBadge = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const OptionalBadge = styled.span`
  color: #71717a;
  font-weight: 400;
  font-size: 11px;
  margin-left: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: #1a1025;
  border: 1px solid #3f3f46;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #9535fc;
    background: #24102f;
  }
  
  &::placeholder {
    color: #52525b;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  background: #1a1025;
  border: 1px solid #3f3f46;
  border-radius: 8px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  resize: vertical;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: #9535fc;
    background: #24102f;
  }
  
  &::placeholder {
    color: #52525b;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const SubmitButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, #9535fc 0%, #7c3aed 100%);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 14px 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(149, 53, 252, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: #a1a1aa;
  border: 1px solid #3f3f46;
  border-radius: 8px;
  padding: 14px 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #71717a;
    color: #ffffff;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SuccessIcon = styled.img`
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  object-fit: contain;
`;

const SuccessTitle = styled.h3`
  color: #21c95e;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  margin: 0 0 8px 0;
`;

const SuccessText = styled.p`
  color: #a1a1aa;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  margin: 0 0 20px 0;
`;

const CloseButton = styled.button`
  background: #21c95e;
  color: #000000;
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #34d399;
  }
`;

const EmailFromPrivy = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: rgba(33, 201, 94, 0.1);
  border: 1px solid rgba(33, 201, 94, 0.3);
  border-radius: 8px;
  color: #21c95e;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
`;

const PrivyBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  background: rgba(33, 201, 94, 0.2);
  border-radius: 4px;
  color: #21c95e;
  text-transform: uppercase;
  font-weight: 600;
`;

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const { user } = usePrivy();
  
  // Get email from Privy user if available
  const privyEmail = user?.email?.address || user?.google?.email || null;
  
  const [email, setEmail] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [telegramHandle, setTelegramHandle] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use Privy email if available, otherwise use manual input
  const effectiveEmail = privyEmail || email;
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate
    if (!effectiveEmail) {
      setError('Please enter your email address');
      return;
    }
    if (!feedback.trim()) {
      setError('Please enter your feedback');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error: insertError } = await supabase
        .from('feedback')
        .insert({
          email: effectiveEmail.toLowerCase().trim(),
          x_handle: xHandle.trim() ? xHandle.trim().replace(/^@/, '') : null,
          telegram_handle: telegramHandle.trim() ? telegramHandle.trim().replace(/^@/, '') : null,
          feedback: feedback.trim(),
          submitted_at: new Date().toISOString(),
        });
      
      if (insertError) {
        console.error('Error submitting feedback:', insertError);
        setError('Failed to submit feedback. Please try again.');
        return;
      }
      
      console.log('✅ Feedback submitted successfully');
      setIsSuccess(true);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleClose = () => {
    // Reset form
    setEmail('');
    setXHandle('');
    setTelegramHandle('');
    setFeedback('');
    setIsSuccess(false);
    setError(null);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <Overlay isOpen={isOpen} onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        {isSuccess ? (
          <SuccessMessage>
            <SuccessIcon src="/mavrk_logo_white.png" alt="Mavrk" />
            <SuccessTitle>Thank You!</SuccessTitle>
            <SuccessText>
              Your feedback has been submitted. We appreciate you taking the time to help us improve Mavrk!
            </SuccessText>
            <CloseButton onClick={handleClose}>Close</CloseButton>
          </SuccessMessage>
        ) : (
          <>
            <Header>Share Your Feedback</Header>
            <Subtitle>
              Help us improve Mavrk! Your feedback is valuable and helps shape the future of our platform.
            </Subtitle>
            
            <form onSubmit={handleSubmit}>
              {/* Email Field */}
              <FormGroup>
                <Label>
                  Email Address
                  <RequiredBadge>*</RequiredBadge>
                </Label>
                {privyEmail ? (
                  <EmailFromPrivy>
                    <span>{privyEmail}</span>
                    <PrivyBadge>From Privy</PrivyBadge>
                  </EmailFromPrivy>
                ) : (
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                )}
              </FormGroup>
              
              {/* X Handle Field */}
              <FormGroup>
                <Label>
                  X (Twitter) Handle
                  <OptionalBadge>optional</OptionalBadge>
                </Label>
                <Input
                  type="text"
                  placeholder="@yourhandle"
                  value={xHandle}
                  onChange={(e) => setXHandle(e.target.value)}
                />
              </FormGroup>
              
              {/* Telegram Handle Field */}
              <FormGroup>
                <Label>
                  Telegram Handle
                  <OptionalBadge>optional</OptionalBadge>
                </Label>
                <Input
                  type="text"
                  placeholder="@yourhandle"
                  value={telegramHandle}
                  onChange={(e) => setTelegramHandle(e.target.value)}
                />
              </FormGroup>
              
              {/* Feedback Field */}
              <FormGroup>
                <Label>
                  Your Feedback
                  <RequiredBadge>*</RequiredBadge>
                </Label>
                <TextArea
                  placeholder="Tell us what you think, report bugs, suggest features..."
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  required
                />
              </FormGroup>
              
              {/* Error Message */}
              {error && (
                <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>
                  {error}
                </div>
              )}
              
              {/* Buttons */}
              <ButtonRow>
                <CancelButton type="button" onClick={handleClose}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </SubmitButton>
              </ButtonRow>
            </form>
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default FeedbackModal;

